import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from './constants';

// Create Task Modal Component
export function CreateTaskModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [responsible, setResponsible] = useState('');
  const [deadline, setDeadline] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = ['HMS', 'Brann', 'Førstehjelp', 'Vedlikehold', 'Opplæring', 'Miljø', 'Kvalitet', 'Annet'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !deadline) {
      toast.error('Tittel og frist er påkrevd');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/tasks`, {
        title,
        description: description || null,
        responsible: responsible || null,
        deadline: new Date(deadline).toISOString(),
        category: category || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Oppgave opprettet');
      onCreated();
    } catch (error) {
      toast.error(t('tasks.errors.couldNotCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-task-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-white mb-4">Ny oppgave</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Tittel *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Hva skal gjøres?"
              autoFocus
              data-testid="task-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Beskrivelse</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              rows={3}
              placeholder="Detaljer om oppgaven (valgfritt)"
              data-testid="task-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Frist *</label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              data-testid="task-deadline-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Ansvarlig</label>
            <input
              type="text"
              value={responsible}
              onChange={(e) => setResponsible(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Navn eller rolle"
              data-testid="task-responsible-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Kategori</label>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(category === cat ? '' : cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    category === cat
                      ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                      : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-cyan-500/50'
                  }`}
                  data-testid={`task-category-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:bg-slate-700 font-medium transition-colors"
              data-testid="cancel-create-task"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="submit-create-task"
            >
              {submitting ? t('common.loading') : t('tasks.create') || 'Opprett'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
